import React, { useState, useEffect, useRef } from 'react';

interface UsernameValidatedComponentProps {
    userName: string;
    setUserName: (value: string) => void;
    className?: any;
    getUsername: (username: string) => Promise<boolean>;
    touched: boolean;
    setTouched: (touched: boolean) => void;
    setIsUserNameExist?: (value: boolean | null) => void;
}

const UsernameValidatedComponent = ({
    userName,
    setUserName,
    getUsername,
    className = {
        parentDiv: "",
        label: "font-semibold text-sm",
        input: "w-full rounded py-2 px-4 mt-2 border-2 text-sm text-black outline-tertiary"
    },
    touched,
    setTouched,
    setIsUserNameExist
}: UsernameValidatedComponentProps) => {
    const [isUsernameAvailable, setIsUsernameAvailable] = useState<boolean | null>(null);
    const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
    const [initialUsername, setInitialUsername] = useState<string>('');
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setInitialUsername(userName);
    }, [userName]);

    useEffect(() => {
        let debounceTimer: NodeJS.Timeout;

        if (touched && userName?.trim() !== '' && userName?.trim() !== initialUsername) {
            debounceTimer = setTimeout(() => {
                checkUsernameAvailability(userName?.trim());
            }, 1000);
        } else {
            setIsUsernameAvailable(null);
            setShowErrorMessage(false);
        }

        return () => clearTimeout(debounceTimer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userName, touched]);

    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setUserName(value);
        setTouched(true);
        setIsUsernameAvailable(null);
        setIsUserNameExist && setIsUserNameExist(null);
        setShowErrorMessage(false);
        setShowSuccessMessage(false);
        e.target.setCustomValidity("");
    };

    const checkUsernameAvailability = (username: string) => {
        getUsername(username)
            .then((response: boolean) => {
                setIsUsernameAvailable(!response);
                setIsUserNameExist && setIsUserNameExist(response);
                setShowErrorMessage(response);
                if (!response) {
                    setShowSuccessMessage(true);
                    setTimeout(() => {
                        setShowSuccessMessage(false);
                    }, 3000);
                }
                if (inputRef.current) {
                    inputRef.current.setCustomValidity(response ? "Username is already exist" : "");
                }
            })
            .catch((error: Error) => {
                console.error('Error checking username availability:', error);
                setIsUsernameAvailable(null);
                setShowErrorMessage(false);
                setShowSuccessMessage(false);
            });
    };

    return (
        <div className={className.parentDiv}>
            <label className={className.label}>User Name*</label>
            <input
                ref={inputRef}
                name="username"
                type="text"
                required
                className={className.input}
                placeholder="User Name"
                value={userName}
                onChange={handleUsernameChange}
                autoComplete="new-username"
            />
            {showSuccessMessage && isUsernameAvailable === true && (
                <div className="flex items-center mt-2 text-xs text-green-500">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        className="mr-1"
                        fill="currentColor"
                    >
                        <circle cx="12" cy="12" r="10" opacity="0.15" />
                        <path d="M10.0 15.0L6.8 11.8L8.2 10.4L10.0 12.2L15.8 6.4L17.2 7.8L10.0 15.0Z" />
                    </svg>
                    <span>Username is available</span>
                </div>
            )}
            {showErrorMessage && isUsernameAvailable === false && (
                <div className="flex items-center mt-2 text-xs text-red-500">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        className="mr-1"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <circle cx="12" cy="16" r="1" fill="currentColor" stroke="none" />
                    </svg>
                    <span>Username is already exist</span>
                </div>
            )}
        </div>
    );
};

export default UsernameValidatedComponent;